import React from 'react';
import { Button, Select, Typography, Space } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { cn } from '@/lib/utils';

const { Title, Text } = Typography;

interface BaseDashboardProps {
  title: string;
  children: React.ReactNode;
  productionPeriod?: string;
  onProductionPeriodChange?: (period: string) => void;
  productionPeriods?: { label: string; value: string }[];
  headerExtra?: React.ReactNode;
}

export function BaseDashboard({
  title,
  children,
  productionPeriod,
  onProductionPeriodChange,
  productionPeriods = [],
  headerExtra,
}: BaseDashboardProps) {
  return (
    <div className="w-full flex flex-col min-h-screen bg-[#f5f6f8] dark:bg-[#141414] transition-colors duration-300">
      {/* Hero Banner Header */}
      <div className="bg-gradient-to-r from-[#0d0d0f] to-[#1c1c1f] text-white px-8 py-4 flex flex-col md:flex-row items-center justify-between gap-6 flex-shrink-0 shadow-sm relative overflow-hidden border-b border-white/10">
        {/* Decorative background gradients */}
        <div className="absolute right-0 top-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="absolute left-0 bottom-0 w-48 h-48 bg-[#FF3737]/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />

        <div className="flex flex-col relative z-10">
          <p className="text-[10px] text-white/50 uppercase tracking-widest font-bold mb-2">
            Dashboard
          </p>
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">
            {title}
          </h1>
         
        </div>

        <div className="flex items-center gap-3 relative z-10 flex-shrink-0 flex-wrap justify-center md:justify-end">
          {productionPeriod && onProductionPeriodChange && productionPeriods.length > 0 && (
            <div className="flex flex-col items-start gap-1">
              <span className="text-xs text-white/60 uppercase tracking-wider font-semibold">Período de Produção</span>
              <Select
                value={productionPeriod}
                onChange={onProductionPeriodChange}
                options={productionPeriods}
                className="w-56 [&_.ant-select-selector]:bg-white/15 [&_.ant-select-selector]:border-white/30 [&_.ant-select-selector]:text-white [&_.ant-select-selection-item]:text-white"
                popupMatchSelectWidth={false}
              />
            </div>
          )}

          {headerExtra}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="px-3 sm:px-4 md:px-6 xl:px-8 py-6 w-full max-w-[2000px] mx-auto space-y-8">
          {/* Dashboard Content */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-700 w-full">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

interface BentoGridProps {
  children: React.ReactNode;
  className?: string;
}

BaseDashboard.BentoGrid = function BentoGrid({ children, className }: BentoGridProps) {
  return (
    <div
      className={cn(
        'grid gap-4 md:gap-6',
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
        'auto-rows-auto w-full',
        className
      )}
    >
      {children}
    </div>
  );
};

interface BentoItemProps {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

BaseDashboard.BentoItem = function BentoItem({ children, className, size = 'md' }: BentoItemProps) {
  const sizeClasses = {
    sm: 'col-span-1 row-span-1',
    md: 'col-span-1 md:col-span-2 row-span-1',
    lg: 'col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2 row-span-1',
    xl: 'col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-3 2xl:col-span-3 row-span-1',
    '2xl': 'col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-5 row-span-2',
  };

  return (
    <div className={cn(sizeClasses[size], 'h-full min-h-[160px] w-full transition-all duration-300', className)}>
      {children}
    </div>
  );
};

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  onViewDetails?: () => void;
  className?: string;
  icon?: React.ReactNode;
}

BaseDashboard.ChartCard = function ChartCard({ title, children, onViewDetails, className, icon }: ChartCardProps) {
  return (
    <div
      className={cn(
        'rounded-[20px] bg-white/70 dark:bg-zinc-900/60 backdrop-blur-md border border-[rgba(255,55,55,0.1)] dark:border-white/10 shadow-sm p-6 h-full flex flex-col',
        className
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <Space size={8}>
          {icon}
          <Text className="font-bold tracking-tight text-[#0f172a] dark:text-white">{title}</Text>
        </Space>
        {onViewDetails && (
          <Button type="link" size="small" onClick={onViewDetails} icon={<RightOutlined />} iconPosition="end">
            Ver detalhes
          </Button>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
};

interface InfoCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onViewDetails?: () => void;
  className?: string;
}

BaseDashboard.InfoCard = function InfoCard({ title, children, icon, onViewDetails, className }: InfoCardProps) {
  return (
    <div
      className={cn(
        'rounded-[24px] bg-white/60 dark:bg-zinc-950/60 backdrop-blur-lg border border-[rgba(255,55,55,0.08)] dark:border-white/5 shadow-xl p-6 h-full flex flex-col',
        className
      )}
    >
      <div className="flex items-center justify-between mb-6">
        <Space size={12}>
          {icon && <div className="p-2 rounded-xl bg-[#FF3737]/10 text-[#FF3737] dark:text-[#FF3737]">{icon}</div>}
          <Text className="font-bold tracking-tight text-[#0f172a] dark:text-white">{title}</Text>
        </Space>
        {onViewDetails && (
          <Button type="link" size="small" onClick={onViewDetails} icon={<RightOutlined />} iconPosition="end">
            Ver detalhes
          </Button>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
};

interface SectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

BaseDashboard.Section = function Section({ title, icon, children, className }: SectionProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <div className="flex items-center gap-3 border-b border-[rgba(255,55,55,0.1)] dark:border-white/5 pb-4">
        {icon && <div className="text-[#FF3737] dark:text-[#FF3737] text-xl flex items-center">{icon}</div>}
        <Title level={4} style={{ margin: 0 }} className="font-extrabold tracking-tight text-[#0f172a] dark:text-white">
          {title}
        </Title>
      </div>
      <div>{children}</div>
    </div>
  );
};